import { useCallback, useMemo } from 'react';

import uniqBy from 'lodash-es/uniqBy';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { useAccount } from '@/hooks/useAccount';
import { useDeskId } from '@/hooks/useDeskId';
import { usePreferences } from '@/hooks/usePreferences';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useDepartments } from '@/modules/departments/hooks/useDepartments';
import { selectAccountSlug } from '@/redux/session/selectors';
import { selectUserId } from '@/redux/user/selectors';

import { useConversationsCounts } from './useConversationsCounts';
import { useNavMenu } from './useNavMenu';

export const useNav = () => {
  const slug = useSelector(selectAccountSlug);
  const navigate = useNavigate();
  const deskId = useDeskId();
  const userId = useSelector(selectUserId);

  const { getUserDepartments } = useUserRoles(userId);
  const userDepartments = getUserDepartments(deskId);

  const { agentsExceptUser: agents } = useAccount();

  const { departments } = useDepartments(deskId);

  const { counts, agentCounts, departmentCounts } = useConversationsCounts(deskId);

  const { accountUserPrefs, updateAccountUserPreferences } = usePreferences();

  // pinned Ids from user prefernces
  const pinnedAgentsIds = useMemo(() => {
    return accountUserPrefs?.desks?.[deskId]?.pinned_agents || [];
  }, [accountUserPrefs, deskId]);

  const pinnedDepartmentsIds = useMemo(() => {
    return accountUserPrefs?.desks?.[deskId]?.pinned_departments || [];
  }, [accountUserPrefs, deskId]);

  // filter agents from `agents` that exist on accountUserPrefs pinned_agents
  const shouldComputeFavoriteAgents = accountUserPrefs?.desks?.[deskId];

  const computeFavoriteAgents = useCallback(() => {
    const filteredAgents = agents.filter((presence) =>
      pinnedAgentsIds?.some((id) => id === presence.agent_id)
    );

    return filteredAgents;
  }, [agents, pinnedAgentsIds]);

  const favoriteAgents = useMemo(
    () => (shouldComputeFavoriteAgents ? computeFavoriteAgents() : []),
    [shouldComputeFavoriteAgents, computeFavoriteAgents]
  );

  const favoriteDepartments = useMemo(() => {
    if (userDepartments) {
      return uniqBy(
        [
          ...userDepartments,
          ...(departments?.filter((department) =>
            pinnedDepartmentsIds?.some((id) => id === department.department_id)
          ) ?? []),
        ],
        'department_id'
      );
    }
    return [];
  }, [departments, pinnedDepartmentsIds, userDepartments]);

  // callbacks
  const onAgentPinClick = useCallback(
    (key: string) => {
      const index = pinnedAgentsIds.findIndex((id) => id === key);

      let newPinnedAgents;
      if (index > -1) {
        // If found, remove the agent
        newPinnedAgents = pinnedAgentsIds.filter((agentKey) => agentKey !== key);
      } else {
        // If not found, add the new agent
        newPinnedAgents = [...pinnedAgentsIds, key];
      }

      const newDesks = {
        [deskId]: { ...accountUserPrefs.desks?.[deskId], pinned_agents: newPinnedAgents },
      };

      updateAccountUserPreferences({ ...accountUserPrefs, desks: newDesks });
    },
    [accountUserPrefs, deskId, pinnedAgentsIds, updateAccountUserPreferences]
  );

  const onDepartmentPinClick = useCallback(
    (key: string) => {
      const index = pinnedDepartmentsIds.findIndex((id) => id === key);

      let newPinnedDepartments;
      if (index > -1) {
        newPinnedDepartments = pinnedDepartmentsIds.filter(
          (departmentKey) => departmentKey !== key
        );
      } else {
        newPinnedDepartments = [...pinnedDepartmentsIds, key];
      }

      const newDesks = {
        [deskId]: { ...accountUserPrefs.desks?.[deskId], pinned_departments: newPinnedDepartments },
      };

      updateAccountUserPreferences({ ...accountUserPrefs, desks: newDesks });
    },
    [accountUserPrefs, deskId, pinnedDepartmentsIds, updateAccountUserPreferences]
  );

  // menus
  const handleConversationItemClick = useCallback(
    (key: string) => {
      navigate(`/${slug}/chats/${deskId}/conversations?category=${key}`);
    },
    [navigate, slug, deskId]
  );

  const {
    mainMenu: conversationsMenu,
    canReadOthers,
    canReadBrain,
    canReadDepartmentsOthers,
  } = useNavMenu({ counts, onItemClick: handleConversationItemClick });

  const handleTeammateItemClick = useCallback(
    (key: string) => {
      if (key === 'brain') {
        navigate(`/${slug}/chats/${deskId}/conversations?brain_id=any`);
      } else {
        navigate(`/${slug}/chats/${deskId}/conversations?agent_id=${key}`);
      }
    },
    [navigate, slug, deskId]
  );

  const { agentMenu: teammatesMenu } = useNavMenu({
    agents,
    agentCounts,
    onPinClick: onAgentPinClick,
    onItemClick: handleTeammateItemClick,
  });

  const { agentMenu: filteredTeammatesMenu } = useNavMenu({
    agents: favoriteAgents,
    agentCounts,
    onItemClick: handleTeammateItemClick,
  });

  const handleDepartmentItemClick = useCallback(
    (key: string) => {
      if (key === 'brain') {
        navigate(`/${slug}/chats/${deskId}/conversations?brain_id=any`);
      } else {
        navigate(`/${slug}/chats/${deskId}/conversations?department_id=${key}`);
      }
    },
    [navigate, slug, deskId]
  );

  const { departmentsMenu } = useNavMenu({
    departments,
    userDepartments,
    onPinClick: onDepartmentPinClick,
    onItemClick: handleDepartmentItemClick,
  });

  const { departmentsMenu: filteredDepartmentsMenu } = useNavMenu({
    departments: favoriteDepartments,
    departmentCounts,
    userDepartments,
    onItemClick: handleDepartmentItemClick,
  });

  return {
    conversationsMenu,
    filteredTeammatesMenu,
    filteredDepartmentsMenu,
    teammatesMenu,
    departmentsMenu,
    pinnedAgents: pinnedAgentsIds,
    pinnedDepartments: pinnedDepartmentsIds,
    canReadOthers,
    canReadBrain,
    canReadDepartmentsOthers,
  };
};
